import { useState, useRef } from "react";
import {
  layoutGroupSchema,
  uid,
  type Page,
  type LayoutGroup,
} from "@proof/core";
import { Select, Button, Control, Toggle } from "./components";
import { InsetsControl } from "./InsetsControl";
import { GroupBounds } from "./GroupBounds";
export function LayoutGroups({
  page,
  onChange,
}: {
  page: Page;
  onChange: (patch: Partial<Page>) => void;
}) {
  const [selected, setSelected] = useState("");
  const groups = page.layoutGroups || [];
  const group = groups.find((group) => group.id === selected) || groups[0];
  const update = (patch: Partial<LayoutGroup>) =>
    onChange({
      layoutGroups: groups.map((item) =>
        item.id === group.id ? { ...item, ...patch } : item,
      ),
    });
  const descendants = new Set(group ? [group.id] : []);
  for (let depth = 0; depth < 24; depth++)
    for (const item of groups)
      if (item.parentId && descendants.has(item.parentId))
        descendants.add(item.id);
  const memberDrag = useRef<
    { groupId: string; id: string; order: string } | undefined
  >(undefined);
  const [dropMember, setDropMember] = useState<string>();
  const parentIds = new Set<string>();
  let parentId = group?.parentId;
  while (parentId && !parentIds.has(parentId)) {
    parentIds.add(parentId);
    parentId = groups.find((item) => item.id === parentId)?.parentId;
  }
  const ancestorLocked = groups.some(
    (item) => parentIds.has(item.id) && item.moveTogether !== false,
  );
  const members = group
    ? [
        ...page.nodes
          .filter((node) => node.layoutGroupId === group.id)
          .map((node) => ({
            id: node.id,
            name: node.name,
            kind: "node",
            order: node.layoutOrder || 0,
          })),
        ...groups
          .filter((item) => item.parentId === group.id)
          .map((item) => ({
            id: item.id,
            name: item.name,
            kind: "group",
            order: item.order,
          })),
      ].sort((a, b) => a.order - b.order)
    : [];
  const memberOrder = JSON.stringify(members.map((item) => item.id));
  const reorder = (id: string, targetId: string, after: boolean) => {
    if (
      !group ||
      group.moveTogether !== false ||
      ancestorLocked ||
      id === targetId
    )
      return;
    const moved = members.find((item) => item.id === id);
    if (!moved) return;
    const next = members.filter((item) => item.id !== id),
      index = next.findIndex((item) => item.id === targetId);
    if (index < 0) return;
    next.splice(index + (after ? 1 : 0), 0, moved);
    if (next.every((item, i) => item.id === members[i].id)) return;
    const nodeOrders = new Map(
      next
        .filter((item) => item.kind === "node")
        .map((item) => [item.id, next.indexOf(item)]),
    );
    const groupOrders = new Map(
      next
        .filter((item) => item.kind === "group")
        .map((item) => [item.id, next.indexOf(item)]),
    );
    onChange({
      nodes: page.nodes.map((node) =>
        nodeOrders.has(node.id)
          ? { ...node, layoutOrder: nodeOrders.get(node.id)! }
          : node,
      ),
      layoutGroups: groups.map((item) =>
        groupOrders.has(item.id)
          ? { ...item, order: groupOrders.get(item.id)! }
          : item,
      ),
    });
  };
  const hasMembers =
    !!group &&
    (page.nodes.some((node) => node.layoutGroupId === group.id) ||
      groups.some((item) => item.parentId === group.id));
  return (
    <details>
      <summary>Layout groups</summary>
      <p className="inspector-note">
        Vertical and horizontal groups reflow visible layers in order. Overlay
        groups retain relative positions. Assign layers in their placement
        controls. Empty groups collapse. Clipping uses the group's resolved
        bounds, including content-sized dimensions.
      </p>
      <Button
        disabled={groups.length >= 2000}
        onClick={() => {
          const added = layoutGroupSchema.parse({
            id: uid("layout"),
            name: `Layout group ${groups.length + 1}`,
            x: page.margin,
            y: page.margin,
            width: Math.max(1, (page.width || 612) - 2 * page.margin),
          });
          onChange({ layoutGroups: [...groups, added] });
          setSelected(added.id);
        }}
      >
        Add layout group
      </Button>
      {group && (
        <>
          <Control label="Editing layout group">
            <Select
              value={group.id}
              onChange={(event) => setSelected(event.target.value)}
            >
              {groups.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </Select>
          </Control>
          <Control label="Layout group name">
            <input
              value={group.name}
              onChange={(event) => update({ name: event.target.value })}
            />
          </Control>
          <Control
            label="Text alignment"
            note="Overrides text alignment for this group's text layers and nested groups that inherit it."
          >
            <Select
              value={group.textAlign || ""}
              onChange={(event) => {
                const textAlign = event.target
                  .value as LayoutGroup["textAlign"];
                const affected = page.nodes.filter(
                  (node) =>
                    node.layoutGroupId &&
                    descendants.has(node.layoutGroupId) &&
                    ["text", "richtext"].includes(node.type),
                );
                if (
                  affected.length &&
                  !window.confirm(
                    `This changes the layout of these variables/layers: ${affected.map((node) => node.name).join(", ")}. Continue?`,
                  )
                )
                  return;
                update({ textAlign: textAlign || undefined });
              }}
            >
              <option value="">Use individual text alignment</option>
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </Select>
          </Control>
          <Toggle
            label="Lock group together"
            value={group.moveTogether !== false}
            onChange={(moveTogether) => update({ moveTogether })}
          />
          <p className="inspector-note">
            {ancestorLocked
              ? "A parent group is locked together. Unlock it to edit members independently."
              : group.moveTogether !== false
                ? "Drag any member on the page to move the whole group. Unlock to edit individual layers and their internal order."
                : "Drag members below to change their internal layout order. Alt + Up / Down also works."}
          </p>
          <div aria-label="Group member order">
            {members.map((member, index) => (
              <button
                key={member.id}
                type="button"
                className="layer-row"
                data-group-member-id={member.id}
                draggable={group.moveTogether === false && !ancestorLocked}
                disabled={group.moveTogether !== false || ancestorLocked}
                style={{
                  cursor: group.moveTogether === false ? "grab" : "default",
                  outline:
                    dropMember === member.id
                      ? "2px solid var(--color-primary,#287d66)"
                      : undefined,
                }}
                onDragStart={(e) => {
                  memberDrag.current = {
                    groupId: group.id,
                    id: member.id,
                    order: memberOrder,
                  };
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData(
                    "application/x-proof-group-member",
                    member.id,
                  );
                }}
                onDragOver={(e) => {
                  if (memberDrag.current?.groupId === group.id) {
                    e.preventDefault();
                    setDropMember(member.id);
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const drag = memberDrag.current;
                  if (
                    drag?.groupId === group.id &&
                    drag.order === memberOrder
                  ) {
                    const bounds = e.currentTarget.getBoundingClientRect();
                    reorder(
                      drag.id,
                      member.id,
                      e.clientY > bounds.top + bounds.height / 2,
                    );
                  }
                  memberDrag.current = undefined;
                  setDropMember(undefined);
                }}
                onDragEnd={() => {
                  memberDrag.current = undefined;
                  setDropMember(undefined);
                }}
                onKeyDown={(e) => {
                  if (!e.altKey || !["ArrowUp", "ArrowDown"].includes(e.key))
                    return;
                  e.preventDefault();
                  e.stopPropagation();
                  const target =
                    members[index + (e.key === "ArrowUp" ? -1 : 1)];
                  if (target)
                    reorder(member.id, target.id, e.key === "ArrowDown");
                }}
              >
                <span aria-hidden="true">↕</span>
                <span>
                  {index + 1}. {member.name}
                  {member.kind === "group" ? " (group)" : ""}
                </span>
              </button>
            ))}
          </div>
          <Control label="Parent layout group">
            <Select
              value={group.parentId || ""}
              onChange={(event) =>
                update({ parentId: event.target.value || undefined })
              }
            >
              <option value="">Page</option>
              {groups
                .filter((item) => !descendants.has(item.id))
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
            </Select>
          </Control>
          <Control label="Group arrangement">
            <Select
              value={group.direction}
              onChange={(event) =>
                update({
                  direction: event.target.value as LayoutGroup["direction"],
                })
              }
            >
              <option value="vertical">Vertical</option>
              <option value="horizontal">Horizontal</option>
              <option value="overlay">Overlay</option>
            </Select>
          </Control>
          <Control label="Group alignment">
            <Select
              value={group.align}
              onChange={(event) =>
                update({ align: event.target.value as LayoutGroup["align"] })
              }
            >
              <option value="start">Start</option>
              <option value="center">Center</option>
              <option value="end">End</option>
            </Select>
          </Control>
          <div className="control-grid">
            {(["x", "y", "offsetX", "offsetY", "gap", "order"] as const).map(
              (key) => (
                <Control key={key} label={`Group ${key}`}>
                  <input
                    type="number"
                    value={group[key] ?? 0}
                    min={key === "gap" ? 0 : -10000}
                    max={10000}
                    step={key === "order" ? 1 : 0.1}
                    onChange={(event) =>
                      update({ [key]: Number(event.target.value) })
                    }
                  />
                </Control>
              ),
            )}
          </div>
          {(["width", "height"] as const).map((key) => (
            <Control key={key} label={`Group ${key} (blank fits content)`}>
              <input
                type="number"
                min={0.001}
                max={14400}
                step={0.1}
                value={group[key] ?? ""}
                onChange={(event) =>
                  update({
                    [key]:
                      event.target.value === ""
                        ? undefined
                        : Number(event.target.value),
                  })
                }
              />
            </Control>
          ))}
          <Toggle
            label="Clip contents to group"
            value={!!group.clipContent}
            onChange={(value) => update({ clipContent: value || undefined })}
          />
          <GroupBounds key={group.id} group={group} onChange={update} />
          {(["anchorX", "anchorY"] as const).map((key) => (
            <Control
              key={key}
              label={`Group ${key === "anchorX" ? "horizontal" : "vertical"} anchor`}
            >
              <Select
                value={group[key] ?? 0}
                onChange={(event) => update({ [key]: event.target.value })}
              >
                <option value="start">Start</option>
                <option value="center">Center</option>
                <option value="end">End</option>
              </Select>
            </Control>
          ))}
          <InsetsControl
            label="Group margins"
            value={group.margins}
            onChange={(value) =>
              update({
                margins:
                  typeof value === "number"
                    ? { top: value, right: value, bottom: value, left: value }
                    : value,
              })
            }
          />
          <Button
            disabled={hasMembers}
            onClick={() =>
              onChange({
                layoutGroups: groups.filter((item) => item.id !== group.id),
              })
            }
          >
            Remove empty group
          </Button>
        </>
      )}
    </details>
  );
}
